import { readCookie } from '@components/common';
import { _CONSTANTS } from '@components/common/constant';
import { getBackendVisiterExpDetail } from 'helper/experiments/experimentActions';
import { useCallback, useEffect, useRef } from 'react';

// Add a cache to store experiment results across hook instances
const experimentCache = new Map<string, any>();

interface ExperimentOptions {
  retryDelay?: number;
  retryLimit?: number;
  shouldRetry?: boolean;
  visitorCookieName?: string;
}

interface ExperimentMap {
  mobile: any;
  desktop: any;
  [key: string]: any;
}

/**
 * Custom hook to conduct experiments based on visitor ID
 *
 * @param deviceType Current device type ('mobile' or 'desktop')
 * @param experimentsMap Mapping of device types to experiment IDs
 * @param userAgent User agent string
 * @param callback Function to call with experiment results
 * @param options Configuration options
 * @returns Object with experiment state and control functions
 */
export const useExperiment = (
  deviceType: string,
  experimentsMap: ExperimentMap | any,
  userAgent: string,
  callback: (experimentData: any) => void,
  options: ExperimentOptions = {}
) => {
  const {
    retryDelay = 500,
    retryLimit = 5,
    shouldRetry = true,
    visitorCookieName = _CONSTANTS.VSTR,
  } = options;

  const retryCount = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRunRef = useRef(false); // Track if this specific instance has run

  // Generate a unique cache key for this experiment configuration
  const cacheKey = useRef(
    `${deviceType}_${JSON.stringify(experimentsMap)}_${userAgent.slice(0, 20)}`
  );

  // Clear any pending timeouts
  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // The core experiment function
  const conductExperiment = useCallback(async () => {
    // Skip if this instance has already run the experiment
    if (hasRunRef.current) return;

    // Check cache first to avoid redundant API calls
    if (experimentCache.has(cacheKey.current)) {
      callback(experimentCache.get(cacheKey.current));
      hasRunRef.current = true;
      return;
    }

    // Try to get the visitor ID
    const visitorId = readCookie(visitorCookieName);

    if (visitorId) {
      try {
        // Check if experimentsMap is an object with mobile/desktop keys or a direct ID
        const experimentConfig =
          typeof experimentsMap === 'object' &&
          (experimentsMap.mobile || experimentsMap.desktop)
            ? experimentsMap
            : { mobile: experimentsMap, desktop: experimentsMap };

        const experimentData = await getBackendVisiterExpDetail(
          deviceType,
          experimentConfig,
          userAgent
        );

        // Cache the result
        experimentCache.set(cacheKey.current, experimentData);
        hasRunRef.current = true;

        // Execute callback with experiment data
        callback(experimentData);
      } catch (error) {
        console.error('Failed to conduct experiment:', error);
      }
    } else if (shouldRetry && retryCount.current < retryLimit) {
      // Retry if no visitor ID and we haven't hit the limit
      retryCount.current += 1;
      timeoutRef.current = setTimeout(conductExperiment, retryDelay);
    }
  }, [
    deviceType,
    experimentsMap,
    userAgent,
    callback,
    visitorCookieName,
    shouldRetry,
    retryLimit,
    retryDelay,
  ]);

  // Run the experiment only once when the hook mounts
  useEffect(() => {
    if (!hasRunRef.current) {
      retryCount.current = 0;
      timeoutRef.current = setTimeout(conductExperiment, 0);
    }

    // Cleanup timeouts on unmount
    return clearTimeouts;
  }, [conductExperiment, clearTimeouts]);

  return {
    runExperiment: conductExperiment,
    cancelExperiment: clearTimeouts,
  };
};
